import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useOrderDraft } from '../Components/Orders/OrderDraftContext';
import { getAllUniqueSizes, getProductSizes } from './functions';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
const VariationTableList = ({ activeMultiplier = 1, multiplierMode = 'plus', searchQuery = '', handleClearQTYForProduct }) => {
    const navigate = useNavigate();
    const { draft, updateCartItemQty, removeSelectedProduct, clearCartForProduct } = useOrderDraft();
    const { selectedProducts, cartItems, selectedCustomer } = draft;

    const [copiedSku, setCopiedSku] = React.useState(null);

    const handleCopy = (sku) => {
        setCopiedSku(sku);
    };

    const handlePaste = (targetSku) => {
        if (!copiedSku) return;
        const targetProduct = selectedProducts.find(p => p.sku === targetSku);
        if (!targetProduct) return;
        const targetSizes = getProductSizes(targetProduct);
        targetSizes.forEach(sizeObj => {
            const copiedItem = cartItems.find(item => item.sku === copiedSku && item.size === sizeObj.size);
            const copiedQty = copiedItem ? copiedItem.unitQuantity : 0;
            updateCartItemQty(targetProduct, sizeObj, copiedQty);
        });
    };

    const uniqueSizes = React.useMemo(() => {
        return getAllUniqueSizes(selectedProducts);
    }, [selectedProducts]);

    const filteredProducts = React.useMemo(() => {
        if (!searchQuery) return selectedProducts;
        const lower = searchQuery.toLowerCase();
        return selectedProducts.filter(product =>
            (product.sku || "").toLowerCase().includes(lower) ||
            (product.StyleFactory || "").toLowerCase().includes(lower)
        );
    }, [selectedProducts, searchQuery]);

    const getAvailableInputsList = React.useCallback(() => {
        const list = [];
        filteredProducts.forEach(product => {
            const availableSizes = getProductSizes(product);
            const sizeMap = {};
            availableSizes.forEach(s => {
                sizeMap[s.size] = s;
            });
            uniqueSizes.forEach(sizeNum => {
                const sizeObj = sizeMap[sizeNum];
                if (sizeObj) {
                    list.push({ product, sizeObj, sku: product.sku, size: sizeNum });
                }
            });
        });
        return list;
    }, [filteredProducts, uniqueSizes]);

    const handleRemoveProduct = (sku) => {
        try {
            removeSelectedProduct(sku)
            clearCartForProduct(sku)
        } catch (error) {
            console.error("Error in handleRemoveProduct: ", error);
        }
    }

    if (!selectedProducts || selectedProducts.length === 0) {
        return (
            <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No products selected yet.
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                    Please go back to the product selection screen to select products for your order.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => {
                        if (selectedCustomer) {
                            navigate(`/SelectAProduct/${selectedCustomer.customerid}/${encodeURIComponent(selectedCustomer.StoreName)}`);
                        } else {
                            navigate('/SelectCustomer');
                        }
                    }}
                    sx={{ fontWeight: 'bold', borderRadius: 2 }}
                >
                    Go Choose Products
                </Button>
            </Paper>
        );
    }

    return (
        <TableContainer
            component={Paper}
            sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                border: '1px solid rgba(0,0,0,0.08)',
                overflowX: 'auto',
                maxHeight: '67vh',
                '&::-webkit-scrollbar': {
                    height: '8px',
                    width: '8px'
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.15)',
                    borderRadius: '4px'
                }
            }}
        >
            <style>{`
                input.qty-input[type=number]::-webkit-inner-spin-button, 
                input.qty-input[type=number]::-webkit-outer-spin-button { 
                    -webkit-appearance: none; 
                    margin: 0; 
                }
                input.qty-input[type=number] {
                    -moz-appearance: textfield;
                }
            `}</style>
            <Table sx={{ minWidth: 800 }} stickyHeader aria-label="variation quantity entry table">
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                fontWeight: '800',
                                fontSize: '0.9rem',
                                backgroundColor: '#f8f9fa',
                                borderBottom: '2px solid rgba(0,0,0,0.12)',
                                padding: 1,
                            }}
                        >

                        </TableCell>

                        <TableCell
                            sx={{
                                fontWeight: '800',
                                fontSize: '0.9rem',
                                backgroundColor: '#f8f9fa',
                                borderBottom: '2px solid rgba(0,0,0,0.12)',
                                minWidth: 190,
                                padding: 0
                            }}
                        >
                            Product Info
                        </TableCell>
                        {uniqueSizes.map((size) => (
                            <TableCell
                                key={size}
                                align="center"
                                sx={{
                                    fontWeight: '800',
                                    fontSize: '0.85rem',
                                    backgroundColor: '#f8f9fa',
                                    borderBottom: '2px solid rgba(0,0,0,0.12)',
                                    p: 0.3,
                                    borderRight: '1px solid rgba(0,0,0,0.04)'
                                }}
                            >
                                {size}
                            </TableCell>
                        ))}
                    </TableRow>

                </TableHead>
                <TableBody>
                    {filteredProducts.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={uniqueSizes.length + 2} align="center" sx={{ py: 6 }}>
                                <Typography variant="h6" color="text.secondary">
                                    No matching products found.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        filteredProducts.map((product) => {
                            const availableSizes = getProductSizes(product);
                            const sizeMap = {};
                            availableSizes.forEach(s => {
                                sizeMap[s.size] = s;
                            });

                            return (
                                <TableRow
                                    key={product.sku}
                                    hover
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >

                                    {/* Product Info Column */}
                                    <TableCell
                                        align="center"
                                        sx={{
                                            p: 1,
                                            borderRight: '1px solid rgba(0,0,0,0.04)',
                                            minWidth: 5
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'left' }}>
                                            <Stack direction="row" spacing={1}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveProduct(product.sku)}
                                                    title="Remove Product"
                                                    sx={{
                                                        backgroundColor: 'error.main',
                                                        color: 'white',
                                                        borderRadius: 1.5,
                                                        width: 30,
                                                        height: 30,
                                                        '&:hover': {
                                                            backgroundColor: 'error.dark',
                                                        }
                                                    }}
                                                >
                                                    <CloseIcon sx={{ fontSize: '1rem' }} />
                                                </IconButton>

                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleClearQTYForProduct(product)}
                                                    title="Clear Quantities"
                                                    sx={{
                                                        backgroundColor: 'grey.500',
                                                        color: 'white',
                                                        borderRadius: 1.5,
                                                        width: 30,
                                                        height: 30,
                                                        '&:hover': {
                                                            backgroundColor: 'grey.700',
                                                        }
                                                    }}
                                                >
                                                    <RemoveShoppingCartIcon sx={{ fontSize: '1rem' }} />
                                                </IconButton>
                                            </Stack>

                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                size="small"
                                                startIcon={<ContentCopyIcon sx={{ fontSize: '0.8rem !important' }} />}
                                                onClick={() => handleCopy(product.sku)}
                                                sx={{
                                                    minWidth: 40,
                                                    fontSize: '0.80rem',
                                                    fontWeight: 'bold',
                                                    py: 0.3,
                                                    px: 1,
                                                    borderRadius: 1.5,
                                                    textTransform: 'none',
                                                    borderColor: copiedSku === product.sku ? 'primary.main' : 'rgba(0,0,0,0.15)',
                                                    backgroundColor: copiedSku === product.sku ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: copiedSku === product.sku ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.04)'
                                                    }
                                                }}
                                            >
                                                {copiedSku === product.sku ? "Copied" : "Copy"}
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                size="small"
                                                disabled={copiedSku === null || copiedSku === product.sku}
                                                startIcon={<ContentPasteIcon sx={{ fontSize: '0.8rem !important' }} />}
                                                onClick={() => handlePaste(product.sku)}
                                                sx={{
                                                    minWidth: 40,
                                                    fontSize: '0.80rem',
                                                    fontWeight: 'bold',
                                                    py: 0.3,
                                                    px: 1,
                                                    borderRadius: 1.5,
                                                    textTransform: 'none'
                                                }}
                                            >
                                                Paste
                                            </Button>
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        sx={{
                                            borderRight: '1px solid rgba(0,0,0,0.06)',
                                            pl: 0.5
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                            <Box
                                                component="img"
                                                src={product.Picture || 'https://via.placeholder.com/150x150?text=No+Image'}
                                                alt={product.StyleFactory}
                                                sx={{
                                                    width: 70,
                                                    height: 70,
                                                    objectFit: 'contain',
                                                    borderRadius: 1.5,
                                                    backgroundColor: '#fdfdfd',
                                                    border: '1px solid rgba(0,0,0,0.04)',
                                                    p: 0
                                                }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = "/blank-image.png";
                                                }}
                                            />
                                            <Box>
                                                <Typography variant="body2" fontWeight="800" color="text.primary" sx={{ fontSize: '1.2rem' }}>
                                                    {product.StyleFactory}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" fontWeight="800" sx={{ fontSize: '1.0rem', mt: 0.2 }}>
                                                    SKU: {product.sku}
                                                </Typography>

                                                {/* Price bands inside cell */}
                                                {/* <Box sx={{ display: 'flex' }}>
                                                    {product.Size1 && product.Price1 && (
                                                        <Typography variant="caption" sx={{ fontWeight: '700', fontSize: '0.7rem', color: '#1976d2' }}>
                                                            {product.Size1}: <br />${product.Price1}
                                                        </Typography>
                                                    )}
                                                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                                    {product.Size2 && product.Price2 && (
                                                        <Typography variant="caption" sx={{ fontWeight: '700', fontSize: '0.7rem', color: '#1976d2' }}>
                                                            {product.Size2}: <br />${product.Price2}
                                                        </Typography>
                                                    )}
                                                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                                    {product.Size3 && product.Price3 && (
                                                        <Typography variant="caption" sx={{ fontWeight: '700', fontSize: '0.7rem', color: '#1976d2' }}>
                                                            {product.Size3}: <br />${product.Price3}
                                                        </Typography>
                                                    )}
                                                </Box> */}
                                            </Box>
                                        </Box>
                                    </TableCell>

                                    {/* Size Columns */}
                                    {uniqueSizes.map((sizeNum) => {
                                        const sizeObj = sizeMap[sizeNum];
                                        const isAvailable = !!sizeObj;

                                        const cartItem = cartItems.find(
                                            item => item.sku === product.sku && item.size === sizeNum
                                        );
                                        const qtyVal = cartItem ? cartItem.unitQuantity : "";

                                        return (
                                            <TableCell
                                                key={sizeNum}
                                                align="center"
                                                sx={{
                                                    p: 0.5,
                                                    borderRight: '1px solid rgba(0,0,0,0.04)',
                                                    backgroundColor: isAvailable ? 'inherit' : 'rgba(0,0,0,0.02)',
                                                    position: 'relative'
                                                }}
                                            >
                                                {isAvailable ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                        <input
                                                            type="number"
                                                            className="qty-input"
                                                            min="0"
                                                            value={qtyVal}
                                                            readOnly={true}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value, 10);
                                                                updateCartItemQty(product, sizeObj, isNaN(val) ? 0 : val);
                                                            }}
                                                            onClick={() => {
                                                                const currentQty = qtyVal ? parseInt(qtyVal, 10) : 0;
                                                                let newQty;
                                                                if (multiplierMode === 'plus') {
                                                                    newQty = currentQty + activeMultiplier;
                                                                } else {
                                                                    newQty = Math.max(0, currentQty - activeMultiplier);
                                                                }
                                                                updateCartItemQty(product, sizeObj, newQty);
                                                            }}
                                                            onFocus={(e) => e.target.select()}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Tab' && !e.shiftKey) {
                                                                    const list = getAvailableInputsList();
                                                                    const currentIndex = list.findIndex(
                                                                        item => item.sku === product.sku && item.size === sizeNum
                                                                    );
                                                                    if (currentIndex !== -1 && currentIndex + 1 < list.length) {
                                                                        const nextItem = list[currentIndex + 1];
                                                                        const currentQty = qtyVal ? parseInt(qtyVal, 10) : 0;
                                                                        updateCartItemQty(nextItem.product, nextItem.sizeObj, currentQty);
                                                                    }
                                                                }
                                                            }}
                                                            style={{
                                                                width: '45px',
                                                                height: '32px',
                                                                border: qtyVal > 0 ? '2px solid #b93636' : '1px solid rgba(0,0,0,0.15)',
                                                                borderRadius: '6px',
                                                                textAlign: 'center',
                                                                fontWeight: qtyVal > 0 ? '800' : 'bold',
                                                                fontSize: '0.85rem',
                                                                color: qtyVal > 0 ? '#b93636' : '#2c3e50',
                                                                backgroundColor: qtyVal > 0 ? 'rgba(185,54,54,0.04)' : 'white',
                                                                outline: 'none',
                                                                boxShadow: qtyVal > 0 ? '0 2px 6px rgba(185,54,54,0.1)' : 'none',
                                                                transition: 'all 0.15s ease',
                                                                cursor: 'pointer'
                                                            }}
                                                        />
                                                        {sizeObj.price ? (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontSize: '0.68rem',
                                                                    fontWeight: '700',
                                                                    color: '#1976d2',
                                                                    mt: 0.9,
                                                                    lineHeight: 1.1,
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                ${String(sizeObj.price).includes('.') ? sizeObj.price : Number(sizeObj.price).toFixed(2)}
                                                            </Typography>
                                                        ) : null}
                                                    </Box>
                                                ) : (
                                                    <span style={{ color: 'rgba(0,0,0,0.2)', fontWeight: 'bold', fontSize: '0.75rem' }}>-</span>
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default VariationTableList;